/**
 * Employee Inbox Page
 *
 * Shows all encrypted submissions for authorities the employee has access to.
 * Allows viewing and decrypting submission data.
 */

import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  useCryptoKeyStore,
  useEncryption,
  isEncryptedProcessVariable,
  EncryptedProcessVariable,
} from '@antragsmanager/crypto-lib';
import {
  getSubmissionsForAuthority,
  SubmissionDto,
  getGKDKForEmployee,
  consumeGKDKDistribution,
} from '../features/encryption/api/keysApi';

export function EmployeeInboxPage() {
  const { authorityId } = useParams<{ authorityId: string }>();
  const [searchParams] = useSearchParams();
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionDto | null>(null);
  const [decryptedData, setDecryptedData] = useState<Record<string, unknown> | null>(null);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [accessibleAuthorities, setAccessibleAuthorities] = useState<string[]>([]);

  const { activeKey, getAccessibleAuthorities, getAuthorityBundle, importGKDKDistribution } =
    useCryptoKeyStore();
  const { decrypt, isReady: encryptionReady } = useEncryption();

  // Get the effective authority ID (from URL param or query string)
  const effectiveAuthorityId = authorityId || searchParams.get('authority');

  // Check accessible authorities
  useEffect(() => {
    if (activeKey) {
      getAccessibleAuthorities().then(setAccessibleAuthorities);
    }
  }, [activeKey, getAccessibleAuthorities]);

  const canDecrypt = useMemo(() => {
    if (!effectiveAuthorityId || !activeKey) return false;
    return accessibleAuthorities.includes(effectiveAuthorityId);
  }, [effectiveAuthorityId, activeKey, accessibleAuthorities]);

  const {
    data: submissions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['submissions', effectiveAuthorityId, activeKey?.keyId],
    queryFn: () => getSubmissionsForAuthority(effectiveAuthorityId!),
    enabled: !!effectiveAuthorityId && !!activeKey?.keyId,
  });

  const handleViewSubmission = (submission: SubmissionDto) => {
    setSelectedSubmission(submission);
    setDecryptedData(null);
    setDecryptionError(null);
  };

  const handleDecrypt = async () => {
    if (!selectedSubmission || !encryptionReady || !effectiveAuthorityId || !activeKey) return;

    setIsDecrypting(true);
    setDecryptionError(null);

    try {
      // Check if the data is encrypted
      if (isEncryptedProcessVariable(selectedSubmission.encryptedData)) {
        // Get authority bundle for decryption
        let authorityBundle = await getAuthorityBundle(effectiveAuthorityId);

        try {
          const decrypted = await decrypt(
            selectedSubmission.encryptedData as EncryptedProcessVariable,
            authorityBundle ?? undefined
          );
          setDecryptedData(decrypted);
        } catch (decryptError) {
          // If decryption fails, try to sync GKDK from backend
          console.log('[Sync] Initial decryption failed, attempting GKDK sync...');
          console.log('[Sync] Error was:', decryptError);
          console.log('[Sync] Fetching GKDK for authority:', effectiveAuthorityId, 'employee:', activeKey.keyId);

          try {
            // Check if there's a pending GKDK distribution on the backend
            const distribution = await getGKDKForEmployee(
              effectiveAuthorityId,
              activeKey.keyId
            );

            console.log('[Sync] Distribution response:', distribution);

            if (distribution) {
              console.log('[Sync] Found GKDK distribution for epoch', distribution.epoch);

              // Import the distribution
              await importGKDKDistribution({
                authorityId: distribution.authorityId,
                epoch: distribution.epoch,
                wrappedBundle: distribution.wrappedBundle as {
                  wrappedBundle: string;
                  iv: string;
                  wrappedByPublicKey: string;
                  authorityName: string;
                },
              });

              // Mark as consumed
              await consumeGKDKDistribution(
                effectiveAuthorityId,
                activeKey.keyId,
                distribution.epoch
              );

              // Retry decryption with new bundle
              authorityBundle = await getAuthorityBundle(effectiveAuthorityId);
              const decrypted = await decrypt(
                selectedSubmission.encryptedData as EncryptedProcessVariable,
                authorityBundle ?? undefined
              );
              setDecryptedData(decrypted);
            } else {
              // No distribution available, throw original error
              throw decryptError;
            }
          } catch (syncError) {
            // If sync also fails, show the error
            console.error('GKDK sync failed:', syncError);
            throw decryptError; // Show original decryption error
          }
        }
      } else {
        // Data is not encrypted, show as-is
        setDecryptedData(selectedSubmission.encryptedData as Record<string, unknown>);
      }
    } catch (err) {
      console.error('Decryption failed:', err);
      setDecryptionError(
        err instanceof Error
          ? err.message
          : 'Die Entschlüsselung ist fehlgeschlagen. Möglicherweise haben Sie keinen Zugriff auf die benötigten Schlüssel.'
      );
    } finally {
      setIsDecrypting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  if (!activeKey) {
    return (
      <Alert severity="warning">
        Sie müssen einen Schlüssel erstellen, um Einreichungen anzusehen.
      </Alert>
    );
  }

  if (!effectiveAuthorityId) {
    return (
      <Alert severity="info">
        Bitte wählen Sie eine Behörde aus, um deren Einreichungen anzusehen.
      </Alert>
    );
  }

  if (!canDecrypt) {
    return (
      <Alert severity="warning">
        Sie haben keinen GKDK-Zugang für diese Behörde. Lassen Sie sich von einem bestehenden
        Mitarbeiter einladen, um Einreichungen entschlüsseln zu können.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Einreichungen: {error instanceof Error ? error.message : 'Unbekannt'}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Eingegangene Anträge</Typography>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Aktualisieren
        </Button>
      </Box>

      {submissions && submissions.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aktenzeichen</TableCell>
                <TableCell>Prozess</TableCell>
                <TableCell>Eingereicht am</TableCell>
                <TableCell>Epoche</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {submission.confirmationNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{submission.processKey}</TableCell>
                  <TableCell>{formatDate(submission.submittedAt)}</TableCell>
                  <TableCell>
                    <Chip label={`E${submission.epoch}`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<LockIcon />}
                      label="Verschlüsselt"
                      size="small"
                      color="info"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ansehen & Entschlüsseln">
                      <IconButton
                        onClick={() => handleViewSubmission(submission)}
                        data-testid={`view-submission-${submission.confirmationNumber}`}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LockIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Keine Einreichungen vorhanden
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Es sind noch keine Anträge für diese Behörde eingegangen.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Submission Detail Dialog */}
      <Dialog
        open={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {decryptedData ? (
              <LockOpenIcon color="success" />
            ) : (
              <LockIcon color="info" />
            )}
            Antrag {selectedSubmission?.confirmationNumber}
          </Box>
        </DialogTitle>
        <DialogContent>
          {decryptionError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="decryption-error">
              {decryptionError}
            </Alert>
          )}

          {!decryptedData && !decryptionError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Die Daten sind verschlüsselt. Klicken Sie auf "Entschlüsseln", um die Inhalte
              anzuzeigen.
            </Alert>
          )}

          {decryptedData && (
            <Box data-testid="decrypted-data">
              <Alert severity="success" sx={{ mb: 2 }} data-testid="decryption-success">
                Daten erfolgreich entschlüsselt
              </Alert>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                {Object.entries(decryptedData).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {key}
                    </Typography>
                    <Typography variant="body1" data-testid={`field-${key}`}>
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSubmission(null)}>Schließen</Button>
          {!decryptedData && (
            <Button
              variant="contained"
              onClick={handleDecrypt}
              disabled={isDecrypting}
              startIcon={isDecrypting ? <CircularProgress size={20} /> : <LockOpenIcon />}
              data-testid="decrypt-button"
            >
              {isDecrypting ? 'Entschlüsseln...' : 'Entschlüsseln'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
